// Offset → IANA best-guess for migration normalizer
const OFFSET_TO_IANA: Record<number, string> = {
  [-720]: 'Etc/GMT+12',
  [-660]: 'Pacific/Pago_Pago',
  [-600]: 'Pacific/Honolulu',
  [-540]: 'America/Anchorage',
  [-480]: 'America/Los_Angeles',
  [-420]: 'America/Denver',
  [-360]: 'America/Chicago',
  [-300]: 'America/New_York',
  [-240]: 'America/Halifax',
  [-180]: 'America/Sao_Paulo',
  [0]: 'UTC',
  [60]: 'Europe/London',
  [120]: 'Europe/Paris',
  [180]: 'Europe/Moscow',
  [210]: 'Asia/Tehran',
  [240]: 'Asia/Dubai',
  [270]: 'Asia/Kabul',
  [300]: 'Asia/Karachi',
  [330]: 'Asia/Kolkata',
  [345]: 'Asia/Kathmandu',
  [360]: 'Asia/Dhaka',
  [420]: 'Asia/Bangkok',
  [480]: 'Asia/Singapore',
  [540]: 'Asia/Tokyo',
  [570]: 'Australia/Adelaide',
  [600]: 'Australia/Sydney',
  [720]: 'Pacific/Auckland',
};

export interface HolidayEntry {
  id: string;
  name: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

export interface ScheduleConfig {
  isEnabled?: boolean;
  startHour: number;
  endHour: number;
  timezone: string;             // IANA string e.g. 'America/New_York'
  weekendBehavior: 'all' | 'sunday' | 'none';
  lunchEnabled: boolean;
  lunchStart: number;
  lunchEnd: number;
  holidayEnabled?: boolean;
  holidays: (string | HolidayEntry)[]; // Supports both legacy and new range format
}

export type StoreStatus = 'open' | 'closed' | 'lunch' | 'holiday';

export interface StatusResult {
  status: StoreStatus;
  hoursUntilOpen?: number;
  totalMinutesUntilOpen?: number;
}

export function calculateStoreStatus(config: ScheduleConfig, _planTier: string = 'free'): StatusResult {
  if (config.isEnabled === false) return { status: 'closed' };

  // Migration normalizer: old configs may still have numeric timezoneOffset.
  // Cast to any to handle legacy shape arriving from old DB rows.
  const raw = config as any;
  let timezone = config.timezone || 'UTC';
  let holidays = config.holidays ?? [];

  if (typeof raw.timezoneOffset === 'number' && !raw.timezone) {
    timezone = OFFSET_TO_IANA[raw.timezoneOffset] ?? 'UTC';
  }
  if (raw.holidayMode === true && !raw.holidays) {
    holidays = raw.holidayReturnDate ? [raw.holidayReturnDate] : [];
  }

  const now = new Date();

  // IANA-aware current date string in store's timezone
  const shopDateStr = now.toLocaleDateString('en-CA', { timeZone: timezone }); // "YYYY-MM-DD"

  // 1. Holiday check (only when toggle is on)
  if (config.holidayEnabled) {
    const isHoliday = holidays.some(h => {
      if (typeof h === 'string') return h === shopDateStr;
      return shopDateStr >= h.startDate && shopDateStr <= h.endDate;
    });
    if (isHoliday) return { status: 'holiday' };
  }

  // IANA-aware time parts
  const shopTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  const hour = shopTime.getHours();
  const minute = shopTime.getMinutes();
  const day = shopTime.getDay();

  const currentTotalMinutes = hour * 60 + minute;

  // 2. Weekend check
  const isWeekend =
    (config.weekendBehavior === 'all' && (day === 0 || day === 6)) ||
    (config.weekendBehavior === 'sunday' && day === 0);

  if (isWeekend) {
    let daysUntilOpen: number;
    if (config.weekendBehavior === 'all') {
      daysUntilOpen = day === 6 ? 2 : 1;
    } else {
      daysUntilOpen = 1;
    }
    const minutesUntilStartOfOpeningDay = ((daysUntilOpen - 1) * 24 * 60) + ((23 - hour) * 60) + (60 - minute);
    const totalMinutes = minutesUntilStartOfOpeningDay + config.startHour * 60;
    return { status: 'closed', hoursUntilOpen: Math.ceil(totalMinutes / 60), totalMinutesUntilOpen: totalMinutes };
  }

  // 3. Business hours check
  const startH = config.startHour;
  const endH = config.endHour;
  const isOvernight = startH > endH;
  const startTotalMinutes = startH * 60;
  const endTotalMinutes = endH * 60;

  let isOpen: boolean;
  if (isOvernight) {
    isOpen = currentTotalMinutes >= startTotalMinutes || currentTotalMinutes < endTotalMinutes;
  } else {
    isOpen = currentTotalMinutes >= startTotalMinutes && currentTotalMinutes < endTotalMinutes;
  }

  // 4. Lunch check
  if (isOpen && config.lunchEnabled && hour >= config.lunchStart && hour < config.lunchEnd) {
    const lunchEndTotalMinutes = config.lunchEnd * 60;
    if (!isOvernight && config.lunchEnd >= endH) {
      const totalMinutesUntilOpen = 24 * 60 - currentTotalMinutes + config.startHour * 60;
      return { status: 'closed', hoursUntilOpen: Math.ceil(totalMinutesUntilOpen / 60), totalMinutesUntilOpen };
    }
    const totalMinutesUntilOpen = lunchEndTotalMinutes - currentTotalMinutes;
    return { status: 'lunch', hoursUntilOpen: Math.ceil(totalMinutesUntilOpen / 60), totalMinutesUntilOpen };
  }

  if (isOpen) {
    return { status: 'open' };
  }

  let totalMinutesUntilOpen: number;
  if (isOvernight) {
    totalMinutesUntilOpen = startTotalMinutes - currentTotalMinutes;
    if (totalMinutesUntilOpen <= 0) totalMinutesUntilOpen += 24 * 60;
  } else if (currentTotalMinutes < startTotalMinutes) {
    totalMinutesUntilOpen = startTotalMinutes - currentTotalMinutes;
  } else {
    totalMinutesUntilOpen = 24 * 60 - currentTotalMinutes + startTotalMinutes;
  }
  return { status: 'closed', hoursUntilOpen: Math.ceil(totalMinutesUntilOpen / 60), totalMinutesUntilOpen };
}
